import { useCallback, useMemo } from 'react';
import uniqBy from 'lodash/uniqBy';

import useScreen from '@lyearn/core/hooks/useScreen';
import {
  ActionMenuItemProps,
  ActionMenuItemType,
  MenuListItems,
  TypographyWithTooltip,
} from '@lyearn/molecules';
import { Box } from '@lyearn/ui';
import { ImageHotspotResponse } from '@/types/schema';

import {
  FIRST_KEYCODE_INDEX,
  OPTION_ITEM_CONFIG,
} from '../../../../components/Player/OptionsListItem/consts';
import { renderKeyCode } from '../../../../components/Player/OptionsListItem/renderers';
import { OptionsListItemTypeEnum } from '../../../../single-select/types';
import { getImageHotspotResponseType } from '../../../utils';
import SubmittedHotspot from '../SubmittedHotspot';
import { ImageHotspotDropdownProps } from './types';

const ImageHotspotDropdown: React.FC<ImageHotspotDropdownProps> = (props) => {
  const {
    question,
    responseText,
    onChange: onChangeProp,
    imageHotspotResponse,
    hotspotId,
    isQuestionSubmitted,
    correctImageHotspotAnswers,
  } = props;

  const { isMobile } = useScreen();

  const imageHotspotComponent = question.imageHotspotComponents.find(
    (hotspotComponent) => hotspotComponent.id === hotspotId,
  )!;

  const type = getImageHotspotResponseType({
    imageHotspotResponse,
    imageHotspotComponent,
    correctImageHotspotAnswers,
  });

  const onChange = useCallback(
    (imageHotspotResponse: ImageHotspotResponse[]) => {
      onChangeProp?.({ imageHotspotResponse });
    },
    [onChangeProp],
  );

  const onAction = useCallback(
    (_: string, item: ActionMenuItemType) => {
      imageHotspotResponse.unshift({
        id: hotspotId,
        // @ts-ignore
        text: (item as ActionMenuItemProps).labelText,
      });
      const updatedImageHotspotResponse = uniqBy(imageHotspotResponse, 'id');

      onChange(updatedImageHotspotResponse);
    },
    [hotspotId, imageHotspotResponse, onChange],
  );

  const items: ActionMenuItemType[] = useMemo(() => {
    const options = question.globalDistractedOptions.reduce<ActionMenuItemType[]>(
      (acc, dist, index) => {
        const keyCode = String.fromCharCode(FIRST_KEYCODE_INDEX + index);

        const type =
          responseText && responseText === dist.text
            ? OptionsListItemTypeEnum.Selected
            : OptionsListItemTypeEnum.Default;
        const { classes, renderKeyCodeContent } = OPTION_ITEM_CONFIG[type];

        const menuItem: ActionMenuItemType = {
          id: dist.id,
          labelText: dist.text,
          // @ts-ignore
          label: (
            <TypographyWithTooltip numberOfLines={1} variant="body-short-02">
              {dist.text}
            </TypographyWithTooltip>
          ),
          startIcon: renderKeyCode({
            className: classes.keyCode,
            children: renderKeyCodeContent(keyCode),
          }),
        };

        isMobile
          ? acc.push(...[menuItem])
          : acc.push(
              ...[
                menuItem,
                ...(index !== question.globalDistractedOptions.length - 1
                  ? [{ type: 'divider' as const }]
                  : []),
              ],
            );

        return acc;
      },
      [],
    );

    return options;
  }, [isMobile, question.globalDistractedOptions, responseText]);

  if (isQuestionSubmitted) {
    return <SubmittedHotspot text={responseText || ''} type={type} />;
  }

  return (
    <Box className="overflow-hidden rounded-8 bg-bg-primary">
      <Box className="h-full max-h-[25.8rem] overflow-y-auto">
        <MenuListItems
          className="xs:space-y-12 space-y-2 px-12"
          items={items}
          menuItemClassName="xs:bg-bg-secondary"
          onAction={onAction}
        />
      </Box>
    </Box>
  );
};

export default ImageHotspotDropdown;
